import { Config } from 'protractor';
import { Injectable } from '@angular/core';
import { of, Observable, from, throwError, forkJoin } from 'rxjs';
import { mergeMap, map, filter } from 'rxjs/operators';
import { catchError } from 'rxjs/operators/catchError';
import FormTemplate from '../entities/form-template.entity';
import { HttpClient, HttpResponse, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import FormTemplateHistory from '../entities/form-template-history.entity';
import { LooseModuleData } from '../contracts/loose-module-data';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { Client } from '../../../../objects/client';
import { defaultIfEmpty } from 'rxjs/operators/defaultIfEmpty';
import { saveAs } from 'file-saver';
import { switchMap, tap } from 'rxjs/operators';
import { LooseCustomField } from '../contracts/loose-custom-field';
import { LooseObject } from '../../../../objects/loose-object';
import { FileService } from '../../../../services/file/file.service';
import { SubscriptionId } from '../../../../objects/subscription-plans';
import { ClientStoreService } from '../../../../services/client-store.service';
import * as moment from 'moment';
import FormTemplatesList from '../collections/form-templates-list.collection';
import { trimEnd, has, isArray, get, isEmpty, isString, } from 'lodash-es';
import { data_get, fallback, filled, when, data_is_true, whenFilled } from '../../../../shared/utils/common';
import { COUNTRIES_WITH_PAGE_FORMAT_THAT_IS_NOT_A4, DOCUMENT_TYPE_PLACEHOLDER_BLOCKS, FORM_TEMPLATE_DISPLAY_ORIENTATION, PAGE_FORMATS, generatePlaceholderBlocks, generateBlockColumn } from '../constants';
import { styles } from '../../admin/common-defaults/styles';
import { header, headerStyles } from '../../admin/common-defaults/header';
import { footer, footerStyles } from '../../admin/common-defaults/footer';
import { clientDefaultDetails, clientDefaultDetailsStyles } from '../../admin/common-defaults/client-details';
import { LiquidFilterUtilsRegistrationService } from '../../../../services/liquid-filter-registration.service';
import { TranslateService } from '@ngx-translate/core';
import { ParseableData } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class FormTemplateService {
  /**
   * @param {HttpClient} http
   * @param {LocalStorageService} storage
   * @param {FormTemplateDataGeneratorService} generator
   * @param {FileService} files
   */
  constructor(
    private http: HttpClient,
    private storage: LocalStorageService,
    private files: FileService,
    private clients: ClientStoreService,
    private _liquidEngine: LiquidFilterUtilsRegistrationService,
    private _translate: TranslateService,
  ) {
    this._liquidEngine.registerFilters();
   }

  /**
   * creates a new form template record
   *
   * @param  {CreateFormTemplateData} data
   *
   * @return {Observable<Form | undefined>}
   */
  createTemplate$(data: CreateFormTemplateData): Observable<FormTemplate | undefined> {
    let payload = new URLSearchParams;

    payload.append('name', data.name);
    payload.append('module', data.module);
    payload.append('isActive', this.transformBooleanValueToStringRepresentation(data.isActive));
    payload.append('isHTML', this.transformBooleanValueToStringRepresentation(data.isHTML));

    if (data.documentType) {
      payload.append('documentType', data.documentType);
    }

    if (data_is_true(data.isHTML)) {
      return this._createTemplateApi(payload);
    } else {
      return this.files.upload(data.file)
        .pipe(
          tap((result) => payload.append('template', result.filename)),
          switchMap(() => this._createTemplateApi(payload))
        );
    }
  }

  getTemplate$(strTemplateId: string): Observable<FormTemplate | undefined> {
    const payload = new URLSearchParams;

    payload.append('template_id', strTemplateId);

    return this.callApi('/get_template', payload).pipe(
      filter((response) => response.status === 200),
      map((response) => this.transformFormTemplate(response.body as FormTemplateResponseBody)),
      catchError((err: HttpErrorResponse) => of(undefined))
    );
  }

  private _createTemplateApi(payload: URLSearchParams): Observable<FormTemplate | undefined> {
    return this.callApi('/create_template', payload).pipe(
      filter((response) => response.status === 201),
      map((response) => this.transformFormTemplate(response.body as FormTemplateResponseBody)),
      catchError((err: HttpErrorResponse) => {
        const templateFileUploadError = this.makeTemplateFileUploadError(err);

        if (!isEmpty(templateFileUploadError)) return throwError(templateFileUploadError);

        return of(undefined);
      })
    );
  }

  /**
   * retrieves the list of form templates based on given filter
   *
   * @param  {any}         filterParams
   * @param  {string|null} page
   *
   * @return {Observable<FormTemplates>}
   */
  list$(filterParams: any, page?: string, per_page?: number): Observable<FormTemplatesList> {
    let body = new URLSearchParams;

    // @todo filterParams
    body.append('page', (page) ? page : '1');

    if (per_page) {
      body.append('per_page', per_page.toString());
    }

    let response$ = this.callApi('/', body.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => this.makeFormTemplatesCollection(response.body)),
      catchError(() => of(this.makeFormTemplatesCollection({ data: [] })))
    );
  }

  /**
   * returns the list of templates that is available or uploaded for the current form
   *
   * @param  {string} formTemplateId
   *
   * @return {Observable<FormTemplateHistory[]>}
   */
  fetchHistories$(formTemplateId: string): Observable<FormTemplateHistory[]> {
    let body = new URLSearchParams;

    body.set('form_template_id', formTemplateId);

    let response$ = this.callApi('/history', body.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => {
        return this.transformHistories(response.body as FormTemplateHistoryResponseBody[]);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * adds a template to the form templates history
   *
   * @param  {string} formTemplateId
   * @param  {File}   templateFile
   *
   * @return {Observable<boolean>}
   */
  addHistory$(formTemplateId: string, templateFile: File): Observable<boolean> {
    let payload = new URLSearchParams;

    payload.append('form_template_id', formTemplateId);

    return this.files.upload(templateFile)
      .pipe(
        tap((result) => payload.append('template', result.filename)),
        switchMap(() => this.callApi('/history/add', payload).pipe(
          map((response) => response.status === 201),
          catchError((err: HttpErrorResponse) => {
            const templateFileUploadError = this.makeTemplateFileUploadError(err);

            if (!isEmpty(templateFileUploadError)) return throwError(templateFileUploadError);

            return of(false);
          })
        ))
      )
  }

  /**
   * sets the given history as default
   *
   * @param  {string} historyId
   *
   * @return {Observable<FormTemplateHistory>}
   */
  setHistoryAsDefault$(historyId: string): Observable<FormTemplateHistory[]> {
    let body = new URLSearchParams;

    body.set('history_id', historyId);

    let response$ = this.callApi('/history/set_as_default', body.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => {
        return this.transformHistories(response.body as FormTemplateHistoryResponseBody[]);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * deletes a given template to our records
   *
   * @param   {string} templateId
   *
   * @returns {Observable<boolean>}
   */
  deleteTemplate$(templateId: string): Observable<boolean> {
    let body = new URLSearchParams;

    body.set('form_template_id', templateId);

    let response$ = this.callApi('/delete', body.toString());

    return response$.pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * retrieves all module active templates
   *
   * @param  {string}  module
   * @param  {boolean} excludeDefaults
   *
   * @return {Observable<FormTemplate[]>}
   */
  getModuleTemplates$(module: string, excludeDefaults: boolean = false): Observable<FormTemplate[]> {
    let body = new URLSearchParams;

    body.set('module', module);

    if (excludeDefaults) {
      body.set('exclude_defaults', '1');
    }

    let response$ = this.callApi('/module/templates', body.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => {
        return (response.body || []).map((response) => {
          return this.transformFormTemplate(response);
        });
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Generates a pdf URL from our form template parser and emits the generated URL which can be
   * used for previewing or downloading the pdf
   *
   * @param {{
   *  module: string
   *  data: LooseModuleData,
   *  template: FormTemplateHistory,
   *  custom: LooseCustomField
   * }} options
   *
   * @returns {Observable<string|null>}
   */
  generatePDFUrl$(options: {
    module: string,
    data: LooseModuleData,
    template?: FormTemplateHistory,
    custom?: LooseCustomField
    template_id?: string,
    additional_document? : string[],
    asHTML?: boolean,
    html_builder_data?: { html: string, css: string },
    display_orientation?: FORM_TEMPLATE_DISPLAY_ORIENTATION,
    page_format?: PAGE_FORMATS,
  }): Observable<string | null> {
    let client = this.getAuthenticatedClient();
    const strClientCountry: string = data_get(client, 'country', { default_value: '' });
    const strDefaultPageFormat: PAGE_FORMATS = fallback(data_get(COUNTRIES_WITH_PAGE_FORMAT_THAT_IS_NOT_A4, strClientCountry), {
      fallback: () => 'a4',
    }) as PAGE_FORMATS;

    let payload = {
      module: options.module,
      template: whenFilled(data_get(options, 'template.path'), {
        then: () => options.template.path,
        else: () => [],
      }),
      data: options.data,
      clientId: (client !== null) ? client['client_id'] : null,
      custom: options.custom,
      additional_document: options.additional_document,
      display_orientation: options.display_orientation || 'landscape',
      page_format: options.page_format || strDefaultPageFormat,
    };

    if (options.template_id) {
      payload['template_id'] = options.template_id;
    }

    if (filled(options.asHTML)) {
      payload['as_html'] = options.asHTML;
    }

    if (filled(options.html_builder_data)) {
      payload['html_builder_data'] = options.html_builder_data;
    }

    let headers: HttpHeaders = new HttpHeaders({
      "Content-Type": "application/json",
      "X-Lambda": "1",
      "X-Authorization": `Bearer ${this.storage.getItem('access_token')}`,
    });

    let response$ = this.http.post<Config>(environment['forms_generator_base_url'], payload, {
      headers,
      observe: 'response',
    });

    return response$.pipe(
      map((response) => {
        return when(data_is_true(options.asHTML), {
          then: () => data_get(response, 'body.html'),
          else: () => data_get(response, 'body.link')
        });
      }),
      catchError(() => {
        return of(null);
      }),
      defaultIfEmpty()
    );
  }

  /**
   * download the current generated PDF using its url
   *
   * @param   {string}           url
   * @param   {string|undefined} outputFilename
   *
   * @returns {Observable<boolean>}
   *
   * @deprecated please use the FileService.downloadFileFromUrl$
   */
  downloadPdf$(url: string, outputFilename?: string): Observable<boolean> {
    return from(fetch(url))
      .pipe(
        filter((response: Response) => response.status === 200),
        switchMap((response: Response) => {
          return from(response.blob());
        }),
        map((blob: Blob) => {
          saveAs(blob, outputFilename);
          return true;
        }),
        catchError(() => of(false))
      );
  }

  /**
   * updates a given form template to its new template data
   *
   * @param   {string}           id
   * @param   {FormTemplateData} updatedData
   *
   * @returns {Observable<FormTemplate|undefined>}
   */
  updateTemplate$(id: string, updatedData: FormTemplateData): Observable<FormTemplate | undefined> {
    let payload = new URLSearchParams;

    payload.set('form_template_id', id);
    payload.set('module', updatedData.module);
    payload.set('isActive', this.transformBooleanValueToStringRepresentation(updatedData.isActive));
    payload.set('isHTML', this.transformBooleanValueToStringRepresentation(data_get(updatedData, 'isHTML', { default_value: false })));
    payload.set('is_default_template', this.transformBooleanValueToStringRepresentation(updatedData.is_default_template));
    payload.set('name', updatedData.name);

    if (updatedData.documentType) {
      payload.set('documentType', updatedData.documentType);
    }

    if (filled(data_get(updatedData, 'display_orientation'))) {
      payload.set('display_orientation', updatedData.display_orientation);
    }

    if (filled(data_get(updatedData, 'page_format'))) {
      payload.set('page_format', updatedData.page_format);
    }

    if (filled(data_get(updatedData, 'html_data'))) {
      payload.set('html_data', updatedData.html_data);
    }

    if (filled(data_get(updatedData, 'css_data'))) {
      payload.set('css_data', updatedData.css_data);
    }

    let response$ = this.callApi('/update_template', payload.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => {
        return this.transformFormTemplate(response.body as FormTemplateResponseBody);
      }),
      catchError(() => of(undefined))
    );
  }

  /**
   * download template file from our storage and allows to be saved
   * by the user
   *
   * @param   {string} templateFilePath
   * @param   {string} outputFilename
   *
   * @returns {Observable<boolean>}
   */
  downloadTemplate$(templateFilePath: string, outputFilename: string): Observable<boolean> {
    let payload = new URLSearchParams;

    payload.set('template_file_path', templateFilePath);

    return this.callApi('/generate_download_url', payload.toString()).pipe(
      filter((response) => response.status === 200),
      map((response) => response.body.url),
      switchMap((url: string) => {
        return from(fetch(url));
      }),
      switchMap((response) => {
        return from(response.blob());
      }),
      map((content: Blob) => {
        saveAs(content, outputFilename);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Gets all the ids of the default form templates
   *
   * @returns {Observable<string[]>}
   */
  getDefaultTemplateIds$(): Observable<string[]> {
    return this.callApi('/get_default_template_ids', '').pipe(
      filter((response) => response.status === 200),
      switchMap((response) => of(response.body.data)),
      catchError(() => of([]))
    );
  }

  /**
   * Gets the record in the 'form_templates_default_html_data' table by the default template id
   *
   * @param {string} strDefaultTemplateId
   *
   * @returns {Observable<LooseObject|null>}
   */
  getFormTemplateDefaultHtmlDataRecord$(strDefaultTemplateId: string): Observable<LooseObject|null> {
    const payload = new URLSearchParams;

    payload.set('template_id', strDefaultTemplateId);

    return this.callApi('/get_form_template_default_html_data_record', payload.toString()).pipe(
      filter((response) => response.status === 200),
      switchMap((response) => of(response.body.data)),
      catchError(() => of(''))
    );
  }

  /**
   * Updates the HTML data of the default document template
   *
   * @param {string} strDefaultTemplateId
   *
   * @returns {Observable<string>}
   */
  upsertFormTemplateDefaultHtmlData$(strDefaultTemplateId: string): Observable<string> {
    const payload = new URLSearchParams;

    payload.set('template_id', strDefaultTemplateId);

    return this.callApi('/upsert_form_template_default_html_data', payload.toString()).pipe(
      filter((response) => response.status === 200),
      switchMap((response) => of(response.body.template_id)),
      catchError(() => of(''))
    );
  }

  /**
   * Transform a url into blob
   *
   * @param {string} url
   *
   * @returns {Observable<Blobl>}
   *
   * @deprecated please use the FileService.toBlob$
   */
  toBlob$(url: string): Observable<Blob> {
    return from(fetch(url))
      .pipe(
        switchMap((response: Response) => from(response.blob()))
      );
  }

  /**
   * retrieves the current authenticated client information from the storage
   *
   * @return {Client|null}
   */
  protected getAuthenticatedClient(): Client | null {
    return (this.storage.hasItem('current_client'))
      ? this.storage.getJsonItem('current_client')
      : null;
  }

  /**
   * wraps and calls the API endpoint
   *
   * @param  {string} uri
   * @param  {any}    body
   * @param  {{
   *  method?: string,
   *  headers?: HttpHeaders,
   *  baseUrl?: string
   * }} options
   *
   * @return {Observable<HttpResponse<Config>>}
   */
  protected callApi(
    uri: string,
    body: any,
    options?: {
      method?: string,
      headers?: HttpHeaders,
      baseUrl?: string
    }
  ): Observable<HttpResponse<Config>> {
    options = Object.assign({
      baseUrl: environment.url,
      method: 'POST'
    }, options)
    uri = (uri !== '/') ? uri : '';
    let baseUrl = trimEnd(options.baseUrl, '/')

    return this.http[options.method.toLowerCase()](`${baseUrl}/form_templates${uri}`, body, { observe: 'response', headers: options.headers });
  }

  /**
   * transforms the form template data into an object of FormTemplate instance
   *
   * @param  {FormTemplateResponseBody} data
   *
   * @return {FormTemplate}
   */
  protected transformFormTemplate(data: FormTemplateResponseBody): FormTemplate {
    return new FormTemplate({
      id: data.id.toString(),
      name: data.name,
      moduleName: data.module,
      isActive: data.isActive || false,
      histories: this.transformHistories(data.histories || []),
      history: (data.history) ? this.transformHistory(data.history) : undefined,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt || null,
      documentType: data.documentType,
      minSubscription: data.minSubscription,
      isHTML: data.isHTML,
      html_data: data.html_data,
      css_data: data.css_data,
      is_custom_template: data.is_custom_template,
      has_odt_template: data.has_odt_template,
      display_orientation: data.display_orientation,
      page_format: data.page_format,
    });
  }

  /**
   * transforms each form templates data into instance of FormTemplate
   *
   * @param  {FormTemplateHistoryResponseBody[]} templates
   *
   * @return {FormTemplateHistory[]}
   */
  protected transformHistories(templates: FormTemplateHistoryResponseBody[]): FormTemplateHistory[] {
    return templates.map((history: FormTemplateHistoryResponseBody) => {
      return this.transformHistory(history);
    });
  }

  /**
   * transform a given template history data into a template history object
   *
   * @param  {FormTemplateHistoryResponseBody} history
   *
   * @return {FormTemplateHistory}
   */
  protected transformHistory(history: FormTemplateHistoryResponseBody): FormTemplateHistory {
    let metadata: FormTemplateMetadataResponseBody = history.metadata;
    const arMetadataFields: FieldResponseBody[] = fallback(data_get(metadata, 'fields'), {
      fallback: () => [],
    });

    return new FormTemplateHistory({
      id: history.id,
      path: history.path,
      version: history.version,
      isDefault: history.isDefault,
      metadata: {
        fields: arMetadataFields.map((field: FieldResponseBody) => {
          let options = field.options;
          return {
            name: field.name,
            text: field.text,
            type: field.type,
            options: {
              defaultValue: options.defaultValue,
              format: options.format || undefined
            }
          }
        }),
      },
    });
  }

  /**
   * creates a form collection instance from a given response body
   *
   * @param  {FormTemplatesCollectionResponseBody} body
   *
   * @return {FormTemplatesList}
   */
  protected makeFormTemplatesCollection(body): FormTemplatesList {
    let templates: FormTemplate[] = body.data.map((data: any) => {
      return this.transformFormTemplate(data);
    });

    return new FormTemplatesList({
      templates,
      current_page: body.current_page,
      total_page: body.total_page,
      has_previous_page: body.has_previous_page,
      has_next_page: body.has_next_page,
      total_records: data_get(body, 'total_records'),
    });
  }

  /**
   * transforms a boolean value to a string representation that will be treated by the
   * API as boolean value
   *
   * '0' - represents false value
   * '1' - represents true value
   *
   * @param {boolean} value
   *
   * @returns {'1'|'0'}
   */
  protected transformBooleanValueToStringRepresentation(value: boolean): '1' | '0' {
    return (value === true) ? '1' : '0';
  }

  protected makeTemplateFileUploadError(err: HttpErrorResponse): UploadTemplateFileError | null {
    if (err.status === 422) {
      const fields = err.error;

      if (has(fields, 'template')) {
        return new UploadTemplateFileError(fields.template[0]); // capture the first error
      }
    }

    return null;
  }

  /**
   * Download the system default template
   *
   * @param {string} module
   * @param {string} documentType
   *
   * @returns {Observable<boolean>}
   */
  downloadSystemTemplate$(module: string, documentType: string): Observable<boolean> {
    let payload = new URLSearchParams;

    payload.set('module', module);
    payload.set('document_type', documentType);

    return this.callApi('/generate_system_template_download_url', payload.toString()).pipe(
      filter((response) => response.status === 200),
      map((response) => response.body),
      switchMap((metadata: SystemTemplateDownloadMetadata) => from(
        fetch(metadata.url)
          .then(response => response.blob())
          .then(content => ({ content, metadata }))
      )),
      map(({ content, metadata }) => {
        saveAs(content, metadata.filename);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Request a restore a speciifc document to system generated template
   *
   * @param   {FormTemplate} template
   *
   * @returns {Observable<boolean>}
   */
  restoreToSystemDefaultTemplate$(template: FormTemplate): Observable<boolean> {
    let payload = new URLSearchParams;

    payload.set('template_id', template.id);

    return this.callApi('/restore_system_default_template', payload.toString())
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * Retrieved current module document data from the server
   *
   * @param {string} id
   * @param {string} module
   * @param {GetModuleDataOptions} options
   *
   * @returns {Observable<GetModuleDataResult>}
   */
  getModuleDocumentData$(id: string, module: string, options: GetModuleDataOptions = {}): Observable<GetModuleDataResult> {
    let payload = new URLSearchParams;

    // set required payload
    payload.set('id', id);
    payload.set('module', module);
    payload.set('timezone', moment().format('Z'));

    if (options.dontIncludeTemplate) {
      payload.set('dont_include_template', '1');
    }

    if (options.document_type) {
      // if received type are multiple types parse it to json
      let documentType = (isArray(options.document_type))
        ? JSON.stringify(options.document_type)
        : options.document_type as string;

      payload.set('document_type', documentType);
    }

    if (options.filter) {
      payload.set('filter', JSON.stringify(options.filter));
    }

    const bIsDefaultReport = data_is_true(
      data_get(options, 'is_default_report'),
    );

    payload.set('is_default_report', bIsDefaultReport ? '1' : '0');

    const currency = get(this.clients.getActiveClient().config, 'currency', 'USD');
    const locale = this.storage.getItem('user_locale') || 'en';

    if (currency) {
      payload.set('currency', currency);
    }

    if (locale) {
      payload.set('locale', locale);
    }

    if (!isEmpty(options.data)) {
      payload.set('data', JSON.stringify(options.data));
    }

    if (options.isHTML) {
      payload.set('is_html', '1');
    }

    return this.callApi('/module/get_data', payload.toString(), { method: 'POST' }).pipe(
      map((response) => {
        response.body.template = (response.body.template) ? this.transformFormTemplate(response.body.template as FormTemplateResponseBody) : [];
        return response;
      }),
      map((response) => ({
        data: response.body.data,
        config: response.body.config,
        template: response.body.template,
        additional_template_paths: response.body.additional_template_paths || [],
        additional_document: response.body.additional_document || [],
        document_sorting: fallback(data_get(response, 'body.document_sorting'), {
          fallback: () => [],
        }),
        ... (filled(data_get(response, 'body.html_builder_data')) && {
          html_builder_data: response.body.html_builder_data
        }),
        ... (filled(data_get(response, 'body.display_orientation'))  && {
          display_orientation: response.body.display_orientation,
        }),
        ... (filled(data_get(response, 'body.page_format'))  && {
          page_format: response.body.page_format,
        }),
      }))
    );
  }

  /**
   * retrieves module default templates
   *
   * @param  {string} module
   * @param  {string|string[]} documentType
   *
   * @return {Observable<FormTemplate|undefined>}
   */
  getSystemTemplate$(module: string, documentType: string | string[]): Observable<FormTemplate | undefined> {
    let body = new URLSearchParams;

    body.set('module', module);
    body.set('document_type', (isArray(documentType)) ? JSON.stringify(documentType) : documentType as string);

    let response$ = this.callApi('/module/get_system_template', body.toString());

    return response$.pipe(
      filter((response) => response.status === 200),
      map((response) => this.transformFormTemplate(response.body['template'] as FormTemplateResponseBody)),
      catchError(() => of(undefined)),
    );
  }

  sendDocumentToEmail$(module: string, start_date: string, end_date: string, filter: LooseObject, documentType: string): Observable<GetModuleDataResult> {
    let payload = new URLSearchParams;

    // set required payload
    payload.set('module', module);
    payload.set('start_date', start_date);
    payload.set('end_date', end_date);
    payload.set('filter', JSON.stringify(filter));
    payload.set('timezone', moment().format('Z'));

    return this.callApi(`/send/${documentType}`, payload.toString(), { method: 'POST' }).pipe(
      map((response) => {
        response.body.template = (response.body.template) ? this.transformFormTemplate(response.body.template as FormTemplateResponseBody) : [];
        return response;
      }),
      map((response) => ({
        data: response.body.data,
        config: response.body.config,
        template: response.body.template,
        additional_template_paths: response.body.additional_template_paths || [],
        additional_document: response.body.additional_document || [],
        document_sorting: fallback(data_get(response, 'body.document_sorting'), {
          fallback: () => [],
        }),

      }))
    );
  }

  /**
   * Retrieved current module document data from the server
   *
   * @param {string} module
   * @param {GetModuleDataOptions} options
   *
   * @returns {Observable<GetModuleDataResult>}
   */
  getModuleDocumentTemplate$(module: string, options: GetModuleDataOptions = {}): Observable<GetModuleDataResult> {
    let payload = new URLSearchParams;

    payload.set('module', module);
    payload.set('timezone', moment().format('Z'));

    if (options.dontIncludeTemplate) {
      payload.set('dont_include_template', '1');
    }

    if (options.document_type) {
      // if received type are multiple types parse it to json
      let documentType = (isArray(options.document_type))
        ? JSON.stringify(options.document_type)
        : options.document_type as string;

      payload.set('document_type', documentType);
    }

    if (options.filter) {
      payload.set('filter', JSON.stringify(options.filter));
    }

    const currency = get(this.clients.getActiveClient().config, 'currency', 'USD');
    const locale = this.storage.getItem('user_locale') || 'en';

    if (currency) {
      payload.set('currency', currency);
    }

    if (locale) {
      payload.set('locale', locale);
    }

    if (!isEmpty(options.data)) {
      payload.set('data', JSON.stringify(options.data));
    }

    if (options.isHTML) {
      payload.set('is_html', '1');
    }

    return this.callApi('/module/get_template', payload.toString(), { method: 'POST' }).pipe(
      map((response) => {
        response.body.template = (response.body.template) ? this.transformFormTemplate(response.body.template as FormTemplateResponseBody) : [];
        return response;
      }),
      map((response) => ({
        data: response.body.data,
        config: response.body.config,
        template: response.body.template,
        additional_template_paths: response.body.additional_template_paths || [],
        additional_document: response.body.additional_document || [],
        document_sorting: fallback(data_get(response, 'body.document_sorting'), {
          fallback: () => [],
        }),
      }))
    );
  }

  createAttachment(id: string, moduleName: string, opts: GetModuleDataOptions = {}): Observable<DocuemntAttachment> {
    return this.getSystemTemplate$(moduleName, opts.document_type).pipe(
      switchMap((template) => this.getModuleDocumentData$(id, moduleName, {
        ... opts,
        isHTML: template.isHTML,
      })),
      switchMap((result) => {
        return this.generatePDFUrl$({
          module: moduleName,
          data: result.data,
          template: {
            ... result.template.history,
            path: [
              ... (isString(result.template.history.path) ? [result.template.history.path] : result.template.history.path) as string[],
              ...result.additional_template_paths
            ],
          },
          custom: {
            additional_document: [],
          },
        }).pipe(
          map(url => ({
            url,
            filename: result.config.filename,
          })),
        )
      }),
      switchMap(({url, filename}) => this.files.toBlob$(url).pipe(
        map((blob) => ({
          blob,
          filename,
        }))
      )),
      switchMap(({ blob, filename }) => {
        const outputFilename = `${filename.substring(0, 85)}.pdf`;
        const file = new File([blob], outputFilename, {
          type: 'application/pdf'
        });

        return this.files.upload(file, undefined, outputFilename).pipe(
          map((presigned) => ({
            name: outputFilename,
            size: file.size / 1024,
            type: 'application/pdf',
            upload_name: presigned.filename
          }))
        );
      }),
    );
  }

  async getDefaultCssEditorBody(strInitialCss: string = ''): Promise<string> {
    const client = this.clients.getActiveClient();

    const strDefaultHeaderBackgroundColor = data_get(
      client,
      'config.pdf.company_header_background_colour',
      { default_value: '#0e76bc' }
    );

    const strDefaultHeaderTextColor = data_get(
      client,
      'config.pdf.company_header_text_colour',
      { default_value: '#ffffff' }
    );

    const strDefaultTitleTextColor = data_get(
      client,
      'config.pdf.company_title_text_colour',
      { default_value: '#0e76bc' }
    );

    const parsedStyles = await this._liquidEngine.engine.parseAndRender(styles, {
      title_text_colour: strDefaultTitleTextColor,
      header_background_colour: strDefaultHeaderBackgroundColor,
      header_text_colour: strDefaultHeaderTextColor,
    });

    return strInitialCss + '\n' + parsedStyles + '\n' + headerStyles + '\n' + clientDefaultDetailsStyles + '\n' + footerStyles;
  }

  async getDefaultHtmlEditorBody(strModule: string, strDocumentType: string): Promise<string> {
    switch (strModule) {
      case 'checklists':
        strDocumentType = 'checklists';
        break;
      case 'opportunities':
        strDocumentType = 'quotation';
        break;
    }

    const client = this.clients.getActiveClient();

    const parseableData = new ParseableData(client);

    let strDefaultContent: string = '';

    if (filled(data_get(DOCUMENT_TYPE_PLACEHOLDER_BLOCKS, `${strModule}.${strDocumentType}`))) {
      const objDefaultReport = DOCUMENT_TYPE_PLACEHOLDER_BLOCKS[strModule][strDocumentType];

      strDefaultContent = generatePlaceholderBlocks(
        `${data_get(objDefaultReport, 'block', { default_value: '' })}-container`,
        this._translate.instant(data_get(objDefaultReport, 'label', { default_value: '' }))
      );
    }

    const defaultBody = generateBlockColumn(1, {
      is_default_container: true,
      default_content: strDefaultContent,
    });

    const parsedHeader = await this._liquidEngine.engine.parseAndRender(header, {
      client: parseableData.client,
    });

    const parsedClientDetails = await this._liquidEngine.engine.parseAndRender(clientDefaultDetails, {
      client: parseableData.client,
    });

    const parsedFooter = await this._liquidEngine.engine.parseAndRender(footer, {
      client: parseableData.client,
    });

    return parsedHeader + '\n' + parsedClientDetails + '\n' + defaultBody + '\n' + parsedFooter;
  }
}

interface FormTemplateHistoryResponseBody {
  /**
   * contains the unique identifier for the form template uploade
   *
   * @var {string}
   */
  id: string,

  /**
   * contains the path of the file template
   *
   * @var {string}
   */
  path: string;

  /**
   * determines if the current form template is set as default
   *
   * @var {boolean}
   */
  isDefault: boolean;

  /**
   * this contains our form template history version
   *
   * @var {string}
   */
  version: string;

  /**
   * contains the form creation date
   *
   * @var {string}
   */
  createdAt: string;

  /**
   * template metadata
   *
   * @var {FormTemplateMetadataResponseBody}
   */
  metadata: FormTemplateMetadataResponseBody;

  /**
   * contains the form updated date
   *
   * @var {string|undefined}
   */
  updatedAt?: string;
}

interface FormTemplateMetadataResponseBody {
  /**
   * contains the fields of the template history metadata
   *
   * @var {FieldResponseBody[]}
   */
  fields: FieldResponseBody[]
}

interface FieldResponseBody {
  /**
   * contains the field label
   *
   * @var {string}
   */
  text: string;

  /**
   * contains the name of the field
   *
   * @var {string}
   */
  name: string;

  /**
   * contains the type of field
   *
   * @var {string}
   */
  type: string;

  /**
   * contains the option information of the field
   *
   * @var {FieldOptionsResponseBody}
   */
  options: FieldOptionsResponseBody,
}

interface FieldOptionsResponseBody {
  /**
   * contains the default value of the field
   *
   * @var {string}
   */
  defaultValue: string;

  /**
   * contains the format of the value that field will accept
   *
   * @var {string}
   */
  format?: string;
}

interface FormTemplateResponseBody {
  /**
   * contains the unique identifier for the current form
   *
   * @var {string|number}
   */
  id: string | number;

  /**
   * contains the name of the form
   *
   * @var {string|number}
   */
  name: string;

  /**
   * contains the module name of the form
   *
   * @var {string|number}
   */
  module: string;

  /**
   * determines if the current for is active
   *
   * @var {boolean}
   */
  isActive: boolean;

  /**
   * contains the form creation date
   *
   * @var {string}
   */
  createdAt: string;

  /**
   * contains the current templates assigned to this form
   *
   * @var {FormTemplateHistoryResponseBody[]}
   */
  histories?: FormTemplateHistoryResponseBody[];

  /**
   * contains the currente default template history
   *
   * @var {FormTemplateHistoryResponseBody}
   */
  history?: FormTemplateHistoryResponseBody;

  /**
   * contains the form updated date
   *
   * @var {string|undefined}
   */
  updatedAt?: string;

  /**
   * contains the form updated date
   *
   * @var {string|null}
   */
  documentType: string | null;

  /**
   * contains the template minimum subscription
   *
   * @var {SubscriptionId|null}
   */
  minSubscription?: SubscriptionId | null;

  /**
   * Tells if the template is a HTML based template
   *
   * @var {boolean}
   */
  isHTML?: boolean;

  /**
   * For HTML document templates, contains the html data
   *
   * @var {string|null}
   */
  html_data?: string|null;

  /**
   * For HTML document templates, contains the css data
   *
   * @var {string|null}
   */
  css_data?: string|null;

  /**
   * Tells if the template is a custom user-added one. All user-added templates in Document Templates have a value of true
   *
   * @var {boolean}
   */
  is_custom_template?: boolean;

  /**
   * Tells if the template has an odt template.
   *
   * @var {boolean}
   */
  has_odt_template?: boolean;

  /**
   * The display orientation of the template, portrait or landscape. Default is landscape.
   *
   * @var {FORM_TEMPLATE_DISPLAY_ORIENTATION}
   */
  display_orientation?: FORM_TEMPLATE_DISPLAY_ORIENTATION;

  /**
   * The paper size of the document, etc: A4, Letter, etc.
   *
   * @var {PAGE_FORMATS}
   */
  page_format?: PAGE_FORMATS;
}

interface FormTemplatesCollectionResponseBody {
  /**
   * contains the associative data for each form
   *
   * @var {FormTemplateResponseBody[]}
   */
  data: FormTemplateResponseBody[];

  /**
   * Optional: contains the next token value for the pagination
   *
   * @var {string | null}
   */
  nextToken?: string | null;

  /**
   * Optional: contains the previous token value for pagination
   *
   * @var {string | null}
   */
  prevToken?: string | null;

  /**
   * Optional: contains the current token requested for pagination
   *
   * @var {string | null}
   */
  currentToken?: string | null;
}

export interface FormTemplateData {
  /**
   * contains the module name for this form template data to be created/updated
   *
   * @var {string}
   */
  module: string;

  /**
   * contains the name of the form template
   *
   * @var {string}
   */
  name: string;

  /**
   * indicates if the current form template should be active/inactive
   *
   * @var {boolean}
   */
  isActive: boolean;

  /**
   * contains the document type of module
   *
   * @var {string | null}
   */
  documentType: string | null;

  /**
   * Tells if the template is a HTML based template
   *
   * @var {boolean}
   */
  isHTML?: boolean;

  /**
   * For HTML document templates, contains the html data
   *
   * @var {string|null}
   */
  html_data?: string|null;

  /**
   * For HTML document templates, contains the css data
   *
   * @var {string|null}
   */
  css_data?: string|null;

  /**
   * Tells if the template is a default one (ie. Job Report)
   *
   * @var {boolean}
   */
  is_default_template?: boolean;

  /**
   * The display orientation of the template, portrait or landscape. Default is landscape.
   *
   * @var {FORM_TEMPLATE_DISPLAY_ORIENTATION}
   */
  display_orientation?: FORM_TEMPLATE_DISPLAY_ORIENTATION;

  /**
   * The paper size of the document, etc: A4, Letter, etc.
   *
   * @var {PAGE_FORMATS}
   */
  page_format?: PAGE_FORMATS;
}

interface CreateFormTemplateData extends FormTemplateData {
  /**
   * contains the template file object
   *
   * @var {File}
   */
  file: File;
}

class UploadTemplateFileError extends Error {
  name: string = 'UploadTemplateFileError';

  constructor(message: string) {
    super(message);
  }
}

interface SystemTemplateDownloadMetadata {
  url: string;
  filename: string;
}

export interface GetModuleDataOptions {
  filter?: { [key: string]: any };
  document_type?: string | string[];
  dontIncludeTemplate?: boolean;
  data?: Record<string, any>;
  isHTML?: boolean;
  /// FC-4537: checks if the report is a system default, like 'Job Report'
  is_default_report?: boolean;
}

export interface GetModuleDataResult {
  data: LooseModuleData;
  config: {
    filename: string
  };
  template: FormTemplate;
  additional_template_paths: string[];

  /**
   * The display orientation of the template, portrait or landscape. Default is landscape.
   *
   * @var {FORM_TEMPLATE_DISPLAY_ORIENTATION}
   */
  display_orientation?: FORM_TEMPLATE_DISPLAY_ORIENTATION;

  /**
   * The paper size of the document, etc: A4, Letter, etc.
   *
   * @var {PAGE_FORMATS}
   */
  page_format?: PAGE_FORMATS;
}

type DocuemntAttachment = {
  name: string;
  size: number;
  type: string;
  upload_name: string;
}